import type { CountryEvent, CountryPeriod } from 'tacho-activity-chart';

export interface PlaceLike {
  type?: number;
  entryTime?: number;
  startCountry?: string | null;
  endCountry?: string | null;
  startCountryCode?: number | null;
  endCountryCode?: number | null;
  startPlaceType?: string | null;
  endPlaceType?: string | null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Converts the new `places` list from either diagram endpoint into country
 * stays plus country-entry events.
 *
 * `type: 0` is a place entry (IN), `type: 1` is a place exit (OUT). Every IN
 * becomes an event marker; consecutive IN/OUT pairs become the country bar.
 */
export function buildCountryChartData(
  places: PlaceLike[] | null | undefined,
  fallbackEnd: number,
): { countries: CountryPeriod[]; countryEvents: CountryEvent[] } {
  const sorted = (Array.isArray(places) ? places : [])
    .filter((place) => Number.isFinite(place?.entryTime) && (place?.entryTime ?? 0) > 0)
    .slice()
    .sort((a, b) => (a?.entryTime ?? 0) - (b?.entryTime ?? 0));

  const countries: CountryPeriod[] = [];
  const countryEvents: CountryEvent[] = [];
  let open: { country: string; countryCode?: string; start: number } | null = null;

  const close = (end: number): void => {
    if (!open) return;
    if (end > open.start) {
      const previous = countries[countries.length - 1];
      if (
        previous &&
        previous.country === open.country &&
        previous.countryCode === open.countryCode &&
        open.start - previous.end <= 60
      ) {
        previous.end = end;
      } else {
        countries.push({
          country: open.country,
          countryCode: open.countryCode,
          start: open.start,
          end,
        });
      }
    }
    open = null;
  };

  for (const place of sorted) {
    const time = Math.floor(place.entryTime ?? 0);
    if (place.type === 0) {
      const country = text(place.startCountry) || text(place.endCountry);
      if (!country) continue;
      close(time);
      open = {
        country,
        countryCode: place.startCountryCode ? String(place.startCountryCode) : undefined,
        start: time,
      };
      countryEvents.push({ country, time });
    } else if (place.type === 1) {
      const country = text(place.endCountry) || text(place.startCountry) || open?.country || '';
      if (open && (!country || country === open.country)) {
        close(time);
      } else if (country) {
        // An exit without a matching entry: still close any open stay.
        close(time);
      }
    }
  }

  close(fallbackEnd);
  return { countries, countryEvents };
}
